import styled from "@emotion/styled";
import { useField } from "formik";
import type { ComponentProps } from "react";
import { useCallback, useEffect, useRef } from "react";
import { default as ReactTextareaAutosize } from "react-textarea-autosize";

import { useFieldContext } from "../FormFieldContext";
import { useBlurOnEnterKey, useHandleBlur } from "./helpers";

/*

  This component overrides the native behavior of FieldTextAreaAutosizeProps
  in two ways:

    - it sets focus on an input if its `autoFocus` prop is set to true,
      even if the element has already been rendered to the DOM.

    - it sets the cursor to the end of the textarea content instead
      of the beginning as some browsers do.

*/

type TextareaAutosizeProps = ComponentProps<typeof ReactTextareaAutosize> & {
  size?: "small" | "large";
};

export const TextareaAutosize = styled((props: TextareaAutosizeProps) => {
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  const autoFocusRef = useRef(props.autoFocus);

  useEffect(() => {
    if (fieldRef.current && !autoFocusRef.current && props.autoFocus) {
      fieldRef.current.focus();
    }
    autoFocusRef.current = props.autoFocus;
  }, [autoFocusRef, props]);

  const setCursorToEnd = (ref: HTMLTextAreaElement | null) => {
    if (!ref) return;
    const length = ref.value.length;
    ref.setSelectionRange(length, length);
  };

  return (
    <ReactTextareaAutosize
      {...props}
      ref={fieldRef}
      onFocus={(e) => {
        props.onFocus?.(e);
        setCursorToEnd(fieldRef.current);
      }}
    />
  );
})`
  resize: none;
  outline: none;
  padding: 3px;
  margin: 0;
  background-color: ${({ theme }) => theme.surface};

  border: 1px solid transparent;

  font-weight: ${({ size }) => (size === "large" ? "600" : "400")};
  font-size: ${({ size, theme }) => (size === "large" ? theme.fontSizeLarge : theme.fontSize)};
  line-height: ${({ size }) => (size === "large" ? "20" : "16")}px;

  &:placeholder-shown {
    background-color: ${({ theme }) => theme.surfaceVariant};
  }

  &:hover:not([readonly]) {
    border-color: ${({ theme }) => theme.outlineSoft};
  }

  &:focus:not([readonly]) {
    border: 1px solid ${({ theme }) => theme.primaryContainer};
    background-color: ${({ theme }) => theme.surface};
  }

  &::placeholder {
    color: ${({ theme }) => theme.generalTextIconVariant};
  }
`;

export type FieldTextareaAutosizeProps = Omit<
  TextareaAutosizeProps,
  "id" | "name" | "value" | "onChange" | "onKeyPress"
>;

export const FieldTextareaAutosize = (props: FieldTextareaAutosizeProps) => {
  const { readOnly, onBlur, maxRows = 5 } = props;

  const { controlId, dataTest, name } = useFieldContext();
  const [{ value, onChange }] = useField(name);

  const handleBlur = useHandleBlur();
  const handleBlurOnEnterKey = useBlurOnEnterKey();

  const handleOnBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      onBlur?.(e);
      handleBlur(e);
    },
    [handleBlur, onBlur],
  );

  return (
    <TextareaAutosize
      {...props}
      id={controlId}
      maxRows={maxRows}
      name={name}
      value={value}
      onBlur={handleOnBlur}
      onChange={onChange}
      onKeyPress={handleBlurOnEnterKey}
      readOnly={readOnly}
      autoComplete="off"
      data-test={`${dataTest ?? name}-textarea`}
    />
  );
};
